import {
  FormNameEnum,
  isNotNull,
  OnboardingStepType,
  OrganizationDetailFragment,
  useStartOnboardingStep,
} from '@alice-financial/alice-sdk'
import { Link, List, ListItem, Typography } from '@alice-financial/pretext-ui'
import * as React from 'react'
import { useParams } from 'react-router'
import { makeOrgFileUploadInputDisplayConfig } from '../../survey/questionTypes/FileUploadInputDisplay'
import { FormInputProps } from '../../survey/types'
import { useCurrentOrgRootPath, useOrg } from '../organization/useOrg'
import { useOrgFormStatus } from '../survey/useOrgFormFields'
import { UploadBankAccounts } from '../UploadBankAccounts'
import { useOrgId } from '../useOrgId'
import { BillableBilling } from './BillableBilling'

type WithId = { id: number }
const sortById = (a: WithId, b: WithId) => {
  if (a.id < b.id) {
    return -1
  }
  if (a.id > b.id) {
    return 1
  }
  return 0
}

const SECURE_FILE_UPLOAD = makeOrgFileUploadInputDisplayConfig({
  questionText: 'Billing accounts file',
  questionSubtext: 'Ensure location names, account numbers, and routing numbers are clearly labeled.',
  questionId: 'secureBillingFile',
  required: false,
})
const SecureFileUpload = SECURE_FILE_UPLOAD.Input
const SECURE_FILE_UPLOAD_POST_UPLOAD = makeOrgFileUploadInputDisplayConfig({
  questionText: 'Billing accounts file (processing)',
  questionSubtext: 'We will enter the billing information you have supplied within 2 business days',
  questionId: 'secureBillingFile',
  required: false,
})
const SecureFileUploadPostUpload = SECURE_FILE_UPLOAD_POST_UPLOAD.Input
const QUESTIONS = [SECURE_FILE_UPLOAD] as const

const UploadBillingAccountsInputs = ({ formName, formVersion }: FormInputProps) => {
  const orgId = useOrgId()
  const { mutate: startOnboardingStep } = useStartOnboardingStep(orgId)
  const formStatus = useOrgFormStatus(orgId, FORM_NAME, FORM_VERSION)

  return formStatus === 'complete' ? (
    <SecureFileUploadPostUpload formName={formName} formVersion={formVersion} />
  ) : (
    <SecureFileUpload
      formName={formName}
      formVersion={formVersion}
      onUpload={() => startOnboardingStep({ stepType: OnboardingStepType.ConnectBilling })}
    />
  )
}

export const uploadBillingAccounts = {
  Page: UploadBillingAccountsInputs,
  questions: QUESTIONS,
}

export const FORM_NAME = FormNameEnum.SecureBilling
export const FORM_VERSION = 1
export const PAGES = [uploadBillingAccounts]

const AddPayGroups = () => {
  const currentOrgRootPath = useCurrentOrgRootPath()
  const org = useOrg()
  if (!org || org.billing?.hasUnifiedBilling || org.isOnboarded) return null
  return (
    <Typography variant="subtitle1">
      Create <strong>additional pay groups</strong> on the{' '}
      <Link to={`${currentOrgRootPath}/locations`}>Locations page</Link>.
    </Typography>
  )
}
export const PaygroupBillingList = ({ org }: { org: OrganizationDetailFragment }) => {
  const { employerId } = useParams()
  const employers = employerId
    ? org.employers.filter((employer) => employer?.id.toString() === employerId)
    : org.employers

  // if (employers.length === 0) return null
  return (
    <>
      <Typography variant="h3" gutterBottom>
        Pay group billing accounts
      </Typography>
      <UploadBankAccounts
        org={org}
        formName={FORM_NAME}
        formVersion={FORM_VERSION}
        pages={PAGES}
        onCompleteReturnTo="billing"
        type="billing"
      />

      <List sx={{ width: '100%' }}>
        {employers
          .filter(isNotNull)
          .sort(sortById)
          .map((employer) => (
            <ListItem key={employer.id} divider>
              <BillableBilling billable={employer} />
            </ListItem>
          ))}
      </List>
      <AddPayGroups />
    </>
  )
}
